<script setup lang="ts">
import { computed } from 'vue'
import { useFormatters } from '@/composables/useFormatters'

const { formatCurrency, formatPercent, formatNumber } = useFormatters()

const props = defineProps<{
  label: string
  value: string | number
  format?: 'currency' | 'percent' | 'number' | 'plain'
  highlighted?: boolean
}>()

const displayValue = computed(() => {
  if (typeof props.value === 'string') return props.value
  switch (props.format) {
    case 'currency':
      return formatCurrency(props.value)
    case 'percent':
      return formatPercent(props.value)
    case 'number':
      return formatNumber(props.value, 2)
    default:
      return String(props.value)
  }
})
</script>

<template>
  <div class="result-card" :class="{ 'result-card--highlighted': highlighted }">
    <dt class="result-card__label">{{ label }}</dt>
    <dd class="result-card__value">{{ displayValue }}</dd>
  </div>
</template>

<style scoped>
.result-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background-color: var(--color-surface);
}

.result-card--highlighted {
  border-color: var(--color-accent);
  background:
    linear-gradient(135deg, rgb(251 191 36 / 0.12), rgb(217 119 6 / 0.06)), var(--color-surface);
  box-shadow: 0 8px 24px rgb(217 119 6 / 0.14);
}

.result-card__label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  font-weight: var(--font-medium);
}

.result-card__value {
  font-size: var(--text-2xl);
  font-weight: var(--font-extrabold);
  color: var(--color-text);
  margin: 0;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  /* Long currency strings (e.g. "R 1 241 456.00") must not overflow
     narrow cards on small phones. */
  overflow-wrap: break-word;
}

.result-card--highlighted .result-card__value {
  color: var(--color-accent-dark);
  font-size: var(--text-3xl);
}

@media (max-width: 479px) {
  .result-card__value {
    font-size: var(--text-xl);
  }

  .result-card--highlighted .result-card__value {
    font-size: var(--text-2xl);
  }
}
</style>
